import forOwn from 'lodash/forOwn';
import range from 'lodash/range';
import findIndex from 'lodash/findIndex';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  Output,
  PLATFORM_ID,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import {
  CustomComponent,
  CUSTOM_NOTE_CONTEXT,
  IBakeryCountry,
  IProductTypeOverview,
  ngHelper,
  UiContextService,
  getImageShape,
  IMAGE_SHAPE_WEIGHT,
} from '@cakeiteasy/web-utils';
import { CART_DELIVERY_TYPES, ICakeCartLine } from '../../../cart';
import { fromEvent, Observable, Subject, firstValueFrom } from 'rxjs';
import { debounceTime, filter, switchMap } from 'rxjs/operators';

@Component({
  standalone: false,
  selector: 'cakes-list',
  encapsulation: ViewEncapsulation.None,
  host: { class: 'ws-product-list' },
  templateUrl: './cakes-list.component.html',
  styleUrls: ['./cakes-list.component.scss'],
})
export class CakesListComponent extends CustomComponent implements OnChanges, AfterViewInit {
  @Input() products: IProductTypeOverview[];
  @Input() cartLines: ICakeCartLine[];
  @Input() currency: string;
  @Input() refName: string;
  @Input() showBakeryLine = true;
  @Input() showNullItem: boolean;
  @Input() showCustomNote = false;
  @Input() noteContext: CUSTOM_NOTE_CONTEXT = CUSTOM_NOTE_CONTEXT.WEBSHOP;

  @Input() bakeryOverviewUrlGenerator: (string) => string;
  @Input() buildProductUrlGenerator: (IProductTypeOverview) => string;

  @Output() onProductOrdered = new EventEmitter<{
    productTypeId: number, productTypeName?: string, orderLineBuilderId?: string,
    /** @Deprecated */
    cartLineId?: number
  }>();
  @Output() onPiecesProductOrdered = new EventEmitter<{ cartLine: ICakeCartLine, productType?: IProductTypeOverview }>();
  @Output() onRemoveOrderLine = new EventEmitter<ICakeCartLine>();
  @Output() onRemoveCateringMenu = new EventEmitter<number>();
  @Output() onNullItemClick = new EventEmitter<void>();
  @Output() onCheckDelivery = new EventEmitter<{
    deliveryType: CART_DELIVERY_TYPES,
    bakeryId: number,
    country: IBakeryCountry,
    productTypeId: number
  }>();

  @ViewChild('itemsWrap', { static: false }) itemsWrap: ElementRef;

  public screenSize: string = null;
  public productColumns: IProductTypeOverview[][];
  public noImageProducts: IProductTypeOverview[];
  public columnFullness: { [key: number]: number };
  public DELIVERY_TYPES = CART_DELIVERY_TYPES;

  constructor(public uiContext: UiContextService,
              @Inject(PLATFORM_ID) private _platformId: any) {
    super();
  }

  ngOnChanges(changes: SimpleChanges): void {
    ngHelper.ifChanged(changes, 'products', () => {
      this.screenSize = null;

      this.distributeProductsToColumns();
      this._defineShowingProducts();
    });
  }

  ngAfterViewInit(): void {
    this.addSubscriber(
      fromEvent(window, 'resize')
        .pipe(debounceTime(300))
        .subscribe(() => this.distributeProductsToColumns()),
    );
  }

  public checkDelivery(deliveryType: CART_DELIVERY_TYPES, productType: IProductTypeOverview) {
    const { bakery: { id: bakeryId, country }, id: productTypeId } = productType;
    this.onCheckDelivery.emit({ deliveryType, country, bakeryId, productTypeId });
  }

  public distributeProductsToColumns() {
    const map = {
      'sm': {
        columnsCount: 1,
        isActual: this.uiContext.isSm(),
        size: 'sm'
      },
      'md': {
        columnsCount: 2,
        isActual: this.uiContext.isMd(),
        size: 'md'
      },
      'lg': {
        columnsCount: 3,
        isActual: () => true,
        size: 'lg'
      }
    };

    for (const key in map) {
      const size = map[key];

      if (size.isActual) {
        if (this.screenSize !== size.size) {
          this.screenSize = size.size;
          this._generateColumns([...this.products], size.columnsCount);
        }

        return;
      }
    }
  }

  private _getBestColumnIndex(columnFullness: { [key: number]: number }): number {
    let minDistance = Number.MAX_SAFE_INTEGER;
    let bestOption = -1;

    forOwn(columnFullness, (fullScore, columnIndex) => {
      const distance = Math.sqrt(Math.pow(fullScore, 2) + Math.pow(+columnIndex, 2));
      if (minDistance > distance) {
        minDistance = distance;
        bestOption = +columnIndex;
      }
    });

    return bestOption;
  }

  private _categorizeProductsByImageAppearance(
    products: IProductTypeOverview[]
  ): { withImage: IProductTypeOverview[], noImage: IProductTypeOverview[] } {
    const noImage = [];
    const withImage = [];

    products.forEach((product) => {
      if (!product.image) {
        noImage.push(product);
      } else {
        withImage.push(product);
      }
    });

    return {
      noImage,
      withImage
    };
  }

  private _generateColumns(products: IProductTypeOverview[], columnsCount = 3) {
    this.columnFullness = {};
    const columns = [];

    range(columnsCount).forEach((i) => {
      this.columnFullness[i] = 0;
      columns.push([]);
    });

    const { noImage, withImage } = this._categorizeProductsByImageAppearance(products);

    if (this.showNullItem) {
      withImage.push({
        id: null,
      } as IProductTypeOverview);
    }

    for (const product of withImage) {
      const bestColumnIndex = this._getBestColumnIndex(this.columnFullness);
      const imageShapeWeight = IMAGE_SHAPE_WEIGHT[getImageShape(product.image)];
      this.columnFullness[bestColumnIndex] += imageShapeWeight * 1000;
      columns[bestColumnIndex].push(product);
    }

    this.noImageProducts = noImage;
    this.productColumns = withImage.length ? columns : [];
  }

  private _defineShowingProducts() {
    let quantityPerColumn = 9;

    if (isPlatformBrowser(this._platformId)) {
      quantityPerColumn = window.screen.height > 1500 ? 9 : 5;
    }

    const showingProducts = this.products && this.products.filter(p => p.isShown) || [];
    if (this.productColumns && showingProducts.length !== this.products.length) {
      this.productColumns.forEach((products) => {
        const lastShowingIndex = findIndex(products, (c) => !c.isShown);

        for (let i = 0; i < quantityPerColumn; i++) {
          if (products[lastShowingIndex + i]) {
            products[lastShowingIndex + i].isShown = true;
          }
        }
      });
    }
  }
}
